/**
 * Single source for mapping the dashboard status foundation's backend
 * `item.type` onto the frontend's category surfaces: the priority list (column
 * label + detail-link kind), the Today's-status rollup (category key) and the
 * page each rollup row opens. All read from here so they can never drift — the
 * "counts reconcile" requirement depends on it.
 */

import type { ChecklistManagerTab } from "@/lib/checklist-manager";
import type { ComplianceManagerTab } from "@/lib/compliance-manager";
import type { EventsHubTab } from "@/lib/events-hub";
import { type FeatureKey, FEATURES } from "@/lib/features";
import type { MonitoringTab } from "@/lib/monitoring";
import type { TaskManagerTab } from "@/lib/task-manager";

/** Detail-link kind the priority list uses to open an item by its type. */
export type PriorityLinkKind =
  | "checklist"
  | "action"
  | "incident"
  | "monitoring"
  | "compliance"
  | "event";

/** A live Today's-status category — also the click-through routing key. */
export type CategoryKey =
  | "checklists"
  | "monitoring"
  | "compliance"
  | "follow-ups"
  | "events";

/**
 * Categories that are gated behind a plan-tier feature. When the org's plan
 * doesn't include the mapped feature, the Today's-status row renders as an
 * "Upgrade" prompt instead of a traffic-light status. The single source
 * linking a dashboard category to the feature key that unlocks it.
 */
export const CATEGORY_FEATURE_MAP: Partial<Record<CategoryKey, FeatureKey>> = {
  monitoring: FEATURES.MONITORING,
  compliance: FEATURES.COMPLIANCE_TASKS,
};

/**
 * A Today's-status row's click-through target. Pairing each route with its own
 * tab union rejects a tab that route doesn't accept — enforced where the map
 * below is declared, since reading `to` and `tab` separately at a call site
 * loses the correlation. The tab types are the ones the routes' `validateSearch`
 * allow-lists are built from.
 */
export type CategoryDestination =
  | { to: "/checklist-manager"; tab: ChecklistManagerTab }
  | { to: "/monitoring"; tab: MonitoringTab }
  | { to: "/compliance-manager"; tab: ComplianceManagerTab }
  | { to: "/task-manager"; tab: TaskManagerTab }
  | { to: "/event-records"; tab: EventsHubTab };

/**
 * The page each live Today's-status row opens. Filtering is tab-level only —
 * the hubs' category, status and sort filters are component state, not search
 * params. Coming-soon categories have no entry, which keeps them inert.
 */
export const CATEGORY_DESTINATION = {
  checklists: { to: "/checklist-manager", tab: "due-soon" },
  monitoring: { to: "/monitoring", tab: "alerts" },
  compliance: { to: "/compliance-manager", tab: "open" },
  "follow-ups": { to: "/task-manager", tab: "follow-up-actions" },
  // Awaiting acknowledgement rather than the Hub's own Open default: the rollup row
  // is a prompt to act, and this tab is where the red events are. An amber-only
  // Events row lands on an empty tab whose copy still reads sensibly — the same
  // trade-off compliance ("open") and monitoring ("alerts") already make.
  events: { to: "/event-records", tab: "awaiting-acknowledgement" },
} as const satisfies Record<CategoryKey, CategoryDestination>;

/**
 * The destination for a row key, or `undefined` for a coming-soon row. Returns
 * an optional so the caller's guard is compiler-enforced — indexing the map with
 * a cast would type the result as always-defined.
 */
export function categoryDestination(
  key: string,
): CategoryDestination | undefined {
  return Object.hasOwn(CATEGORY_DESTINATION, key)
    ? CATEGORY_DESTINATION[key as CategoryKey]
    : undefined;
}

interface TypeMeta {
  /** Priority-list column label. */
  label: string;
  /** Priority-list detail-link kind. */
  kind: PriorityLinkKind;
  /** Today's-status category this type rolls into. */
  categoryKey: CategoryKey;
}

/** Maps a backend status `item.type` to its priority-list and rollup metadata. */
export const TYPE_META: Record<string, TypeMeta> = {
  checklist: {
    label: "Checklists",
    kind: "checklist",
    categoryKey: "checklists",
  },
  "follow-up-action": {
    label: "Follow-up actions",
    kind: "action",
    categoryKey: "follow-ups",
  },
  "out-of-range-incident": {
    label: "Monitoring",
    kind: "incident",
    categoryKey: "monitoring",
  },
  "hot-holding-alert": {
    label: "Monitoring",
    kind: "monitoring",
    categoryKey: "monitoring",
  },
  // One shared type for every failed pass/fail check — emergency lighting,
  // fire extinguisher, and any check registered later. A type missing from this
  // map is silently dropped from the rollup and the priority list, so keeping it
  // source-agnostic means new checks need no frontend change.
  "check-failure-alert": {
    label: "Monitoring",
    kind: "monitoring",
    categoryKey: "monitoring",
  },
  // NAU-410. A per-item-type key rather than one shared "acknowledgement",
  // which inverts the check-failure-alert precedent above — there the
  // destination was identical for every source, whereas here it genuinely
  // differs: a risk-assessment acknowledgement must open the Risk Hub, not an
  // event record. Those keys are one line each when Features 12/13 land.
  //
  // categoryKey "events" folds these into the existing Events rollup row, so
  // CATEGORY_DESTINATION needs no new arm and LIVE_CATEGORIES stays as is. A
  // dedicated "acknowledgements" CategoryKey would need four coordinated edits,
  // and missing the LIVE_CATEGORIES one would silently hide the row.
  //
  // NOTE: the status endpoint must send the EVENT RECORD id as `item.id` here,
  // not the acknowledgement request id — the "event" kind navigates to
  // /event-records/$eventRecordId, so a request id would 404.
  "acknowledgement-event-record": {
    label: "Acknowledgements",
    kind: "event",
    categoryKey: "events",
  },
  "compliance-task": {
    label: "Compliance",
    kind: "compliance",
    categoryKey: "compliance",
  },
  // One shared type across every event type — fire drill, incident, parent
  // complaint and any type seeded later — for the same reason as
  // check-failure-alert above: a per-type string would mean editing this map every
  // time a nursery gains a new kind of event to record.
  "event-record": {
    label: "Events",
    kind: "event",
    categoryKey: "events",
  },
};
